/** Redução de imagens no navegador antes de enviar (logo, ícones, foto de perfil). */

export function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    if (!/^image\/(png|jpeg|webp|svg\+xml)$/.test(file.type)) { reject(new Error("Use uma imagem PNG, JPG, WEBP ou SVG.")); return; }
    if (file.size > 8_000_000) { reject(new Error("Imagem grande demais (máx. 8 MB).")); return; }
    // Lido como data URL (e não blob:), para a mesma imagem poder ser exibida depois na tela de enquadramento.
    const fr = new FileReader();
    fr.onerror = () => reject(new Error("Não foi possível ler a imagem."));
    fr.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Não foi possível ler a imagem."));
      img.src = String(fr.result);
    };
    fr.readAsDataURL(file);
  });
}

const dims = (img: HTMLImageElement) => ({ w: img.naturalWidth || img.width || 512, h: img.naturalHeight || img.height || 512 });

/** Cabe dentro de maxW×maxH mantendo a proporção. */
export function fitDataUrl(img: HTMLImageElement, maxW: number, maxH: number, type: "image/png" | "image/jpeg" | "image/webp" = "image/png", bg?: string, quality = 0.88): string {
  const { w, h } = dims(img);
  const k = Math.min(1, maxW / w, maxH / h);
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(w * k)); c.height = Math.max(1, Math.round(h * k));
  const ctx = c.getContext("2d")!;
  if (bg) { ctx.fillStyle = bg; ctx.fillRect(0, 0, c.width, c.height); }
  ctx.drawImage(img, 0, 0, c.width, c.height);
  return c.toDataURL(type, quality);
}

/** Quadrado size×size: "cover" recorta o centro (foto); "contain" encaixa com margem (ícone). */
export function squareDataUrl(img: HTMLImageElement, size: number, mode: "cover" | "contain", type: "image/png" | "image/jpeg" = "image/png", bg?: string, quality = 0.88): string {
  const { w, h } = dims(img);
  const c = document.createElement("canvas");
  c.width = size; c.height = size;
  const ctx = c.getContext("2d")!;
  if (bg) { ctx.fillStyle = bg; ctx.fillRect(0, 0, size, size); }
  if (mode === "cover") {
    const s = Math.min(w, h);
    ctx.drawImage(img, (w - s) / 2, (h - s) / 2, s, s, 0, 0, size, size);
  } else {
    const pad = Math.round(size * 0.08);
    const k = Math.min((size - 2 * pad) / w, (size - 2 * pad) / h);
    const dw = w * k, dh = h * k;
    ctx.drawImage(img, (size - dw) / 2, (size - dh) / 2, dw, dh);
  }
  return c.toDataURL(type, quality);
}

export async function pickImage(): Promise<File | null> {
  return new Promise((resolve) => {
    const i = document.createElement("input");
    i.type = "file"; i.accept = "image/png,image/jpeg,image/webp,image/svg+xml";
    i.onchange = () => resolve(i.files?.[0] ?? null);
    i.click();
  });
}

// ------------------------------------------------------------ cores e fundo da logo

const toImg = (u: string): Promise<HTMLImageElement> => new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = () => rej(new Error("imagem inválida")); i.src = u; });
const hex2 = (n: number) => n.toString(16).padStart(2, "0");
export const rgbHex = (r: number, g: number, b: number): string => `#${hex2(r)}${hex2(g)}${hex2(b)}`.toUpperCase();

function pixels(img: HTMLImageElement, max = 600): { ctx: CanvasRenderingContext2D; c: HTMLCanvasElement; data: ImageData } {
  const { w, h } = dims(img);
  const k = Math.min(1, max / Math.max(w, h));
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(w * k)); c.height = Math.max(1, Math.round(h * k));
  const ctx = c.getContext("2d", { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0, c.width, c.height);
  return { ctx, c, data: ctx.getImageData(0, 0, c.width, c.height) };
}

/** Cores principais da imagem (ignora transparente), da mais presente para a menos. */
export async function extractPalette(src: string, n = 6): Promise<string[]> {
  const { data } = pixels(await toImg(src), 160);
  const buckets = new Map<string, { r: number; g: number; b: number; n: number }>();
  for (let i = 0; i < data.data.length; i += 4) {
    if (data.data[i + 3]! < 200) continue;
    const r = data.data[i]!, g = data.data[i + 1]!, b = data.data[i + 2]!;
    const key = `${r >> 4}|${g >> 4}|${b >> 4}`;
    const cur = buckets.get(key) ?? { r: 0, g: 0, b: 0, n: 0 };
    cur.r += r; cur.g += g; cur.b += b; cur.n++;
    buckets.set(key, cur);
  }
  const sorted = [...buckets.values()].sort((a, b) => b.n - a.n).map((x) => [Math.round(x.r / x.n), Math.round(x.g / x.n), Math.round(x.b / x.n)] as const);
  const out: Array<readonly [number, number, number]> = [];
  for (const c of sorted) {
    if (out.every((o) => Math.abs(o[0] - c[0]) + Math.abs(o[1] - c[1]) + Math.abs(o[2] - c[2]) > 60)) out.push(c);
    if (out.length >= n) break;
  }
  return out.map((c) => rgbHex(c[0], c[1], c[2]));
}

/** Cor do ponto (x, y em 0..1) da imagem. */
export async function colorAt(src: string, x: number, y: number): Promise<string | null> {
  const { data, c } = pixels(await toImg(src));
  const px = Math.min(c.width - 1, Math.max(0, Math.floor(x * c.width))), py = Math.min(c.height - 1, Math.max(0, Math.floor(y * c.height)));
  const i = (py * c.width + px) * 4;
  return data.data[i + 3]! < 30 ? null : rgbHex(data.data[i]!, data.data[i + 1]!, data.data[i + 2]!);
}

/**
 * Remove o fundo de cor sólida: parte das bordas e apaga (transparente) a área contínua com a mesma cor
 * (tolerância 0..100). Borda suavizada para não ficar serrilhado. Devolve PNG.
 */
export async function removeBackground(src: string, tolerance = 28): Promise<string> {
  const img = await toImg(src);
  const { w, h } = dims(img);
  const k = Math.min(1, 1600 / Math.max(w, h));
  const c = document.createElement("canvas");
  c.width = Math.round(w * k); c.height = Math.round(h * k);
  const ctx = c.getContext("2d", { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0, c.width, c.height);
  const d = ctx.getImageData(0, 0, c.width, c.height);
  const W = c.width, H = c.height, px = d.data;
  // Cor de fundo = mediana das bordas.
  const edge: number[][] = [];
  for (let x = 0; x < W; x += Math.max(1, Math.floor(W / 200))) { edge.push(at(x, 0)); edge.push(at(x, H - 1)); }
  for (let y = 0; y < H; y += Math.max(1, Math.floor(H / 200))) { edge.push(at(0, y)); edge.push(at(W - 1, y)); }
  const med = (i: number) => edge.map((e) => e[i]!).sort((a, b) => a - b)[Math.floor(edge.length / 2)]!;
  const bg = [med(0), med(1), med(2)];
  const tol = (tolerance / 100) * 441;
  const dist = (i: number) => Math.hypot(px[i]! - bg[0]!, px[i + 1]! - bg[1]!, px[i + 2]! - bg[2]!);
  const seen = new Uint8Array(W * H);
  const stack: number[] = [];
  for (let x = 0; x < W; x++) { stack.push(x, (H - 1) * W + x); }
  for (let y = 0; y < H; y++) { stack.push(y * W, y * W + W - 1); }
  while (stack.length) {
    const p = stack.pop()!;
    if (seen[p]) continue;
    const i = p * 4;
    if (px[i + 3]! > 0 && dist(i) > tol) continue;
    seen[p] = 1;
    const x = p % W, y = (p - x) / W;
    if (x > 0) stack.push(p - 1); if (x < W - 1) stack.push(p + 1);
    if (y > 0) stack.push(p - W); if (y < H - 1) stack.push(p + W);
  }
  for (let p = 0; p < W * H; p++) {
    const i = p * 4;
    if (seen[p]) { px[i + 3] = 0; continue; }
    // Borda: vizinho apagado → transparência parcial conforme a proximidade da cor do fundo.
    const x = p % W, y = (p - x) / W;
    const nb = (x > 0 && seen[p - 1]) || (x < W - 1 && seen[p + 1]) || (y > 0 && seen[p - W]) || (y < H - 1 && seen[p + W]);
    if (nb) { const a = Math.min(1, dist(i) / (tol * 2 || 1)); px[i + 3] = Math.round(px[i + 3]! * a); }
  }
  ctx.putImageData(d, 0, 0);
  return trimTransparent(c);
  function at(x: number, y: number) { const i = (y * W + x) * 4; return [px[i]!, px[i + 1]!, px[i + 2]!]; }
}

/** Corta as margens transparentes. */
function trimTransparent(c: HTMLCanvasElement): string {
  const ctx = c.getContext("2d")!;
  const { data } = ctx.getImageData(0, 0, c.width, c.height);
  let x0 = c.width, y0 = c.height, x1 = -1, y1 = -1;
  for (let y = 0; y < c.height; y++) for (let x = 0; x < c.width; x++) {
    if (data[(y * c.width + x) * 4 + 3]! > 10) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  }
  if (x1 < 0) return c.toDataURL("image/png");
  const pad = 6;
  x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad); x1 = Math.min(c.width - 1, x1 + pad); y1 = Math.min(c.height - 1, y1 + pad);
  const o = document.createElement("canvas");
  o.width = x1 - x0 + 1; o.height = y1 - y0 + 1;
  o.getContext("2d")!.drawImage(c, x0, y0, o.width, o.height, 0, 0, o.width, o.height);
  // Limite de tamanho para salvar (logo até ~900 px de largura).
  const k = Math.min(1, 900 / o.width, 300 / o.height);
  if (k >= 1) return o.toDataURL("image/png");
  const f = document.createElement("canvas");
  f.width = Math.round(o.width * k); f.height = Math.round(o.height * k);
  f.getContext("2d")!.drawImage(o, 0, 0, f.width, f.height);
  return f.toDataURL("image/png");
}

/** Contraste WCAG entre duas cores (#RRGGBB). */
export function contrast(a: string, b: string): number {
  const lum = (h: string) => {
    const v = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255).map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
    return 0.2126 * v[0]! + 0.7152 * v[1]! + 0.0722 * v[2]!;
  };
  const [x, y] = [lum(a), lum(b)].sort((p, q) => q - p);
  return (x! + 0.05) / (y! + 0.05);
}
